import { motion, AnimatePresence } from 'framer-motion';
import { X, Map as MapIcon, MessageSquare, Layers, Check, RefreshCw, FileText, Trash2, History, Edit2, Save } from 'lucide-react';
import Map from './Map';
import type { MapData, FormData } from '../types';
import React, { useState, useEffect } from 'react';
import { fetchSubmissionBySerialNumber, applySubmissionToMaps, SubmissionData, getAllStoredSerialNumbers, getAllSerialNumbers, recoverSubmissions, setSubmissionDisplayName } from '../services/submissionService';

interface GroupResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  mapsData: MapData[];
  mapStyles: Record<string, 'color' | 'gray' | 'simple'>;
  recentSubmissions?: string[];
  onRemoveSubmission?: (serialNumber: string) => void;
  onRecoverSubmissions?: (serialNumbers: string[]) => void;
}

const GroupResultsModal = ({ isOpen, onClose, mapsData, mapStyles, recentSubmissions = [], onRemoveSubmission, onRecoverSubmissions }: GroupResultsModalProps) => {
  const [activeMap, setActiveMap] = useState<number>(0);
  const [submissions, setSubmissions] = useState<SubmissionData[]>([]);
  const [activeSubmission, setActiveSubmission] = useState<SubmissionData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'current' | 'submission'>('current');
  const [isEditingActiveSubmission, setIsEditingActiveSubmission] = useState(false);
  const [activeSubmissionEditName, setActiveSubmissionEditName] = useState('');
  const [trashCount, setTrashCount] = useState<number>(0);
  const [showLayersPanel, setShowLayersPanel] = useState(false);
  
  const mapTitles = ['Vista 1 - Cidade', 'Vista 2 - Bairro', 'Vista 3 - Rua'];
  const mapIds = ['city', 'area', 'street'];
  
  // Refs para os mapas
  const mapRefs = React.useRef<(HTMLDivElement | null)[]>([null, null, null]);
  
  // Carrega uma submissão por número de série
  const handleLoadSubmission = async (directSerialNumber?: string) => {
    const serialToUse = directSerialNumber;
    
    if (!serialToUse) {
      setError('Número de série inválido');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const submission = await fetchSubmissionBySerialNumber(serialToUse);
      
      if (submission) {
        // Adiciona à lista se ainda não existir
        setSubmissions(prev => {
          if (!prev.some(s => s.serialNumber === submission.serialNumber)) {
            return [...prev, submission];
          }
          return prev;
        });
        
        setActiveSubmission(submission);
        setViewMode('submission');
      } else {
        setError('Submissão não encontrada');
      }
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Erro ao buscar submissão');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Obtém os mapas modificados com base na submissão selecionada
  const getSubmissionMaps = (): MapData[] => {
    if (!activeSubmission) return mapsData;
    return applySubmissionToMaps(mapsData, activeSubmission);
  };
  
  // Obtém as camadas ativas para um determinado mapa
  const getActiveLayers = (mapIndex: number) => {
    const maps = viewMode === 'submission' && activeSubmission 
      ? getSubmissionMaps() 
      : mapsData;
    
    const map = maps[mapIndex];
    return map.layers.filter(layer => layer.isActive);
  };
  
  // Obtém o estilo do mapa para exibição
  const getMapStyle = (mapIndex: number): 'color' | 'gray' | 'simple' => {
    if (viewMode === 'submission' && activeSubmission) {
      if (mapIndex === 0) return activeSubmission.mapStyles.city;
      if (mapIndex === 1) return activeSubmission.mapStyles.area;
      if (mapIndex === 2) return activeSubmission.mapStyles.street;
    }
    
    return mapStyles[mapIds[mapIndex]];
  };
  
  // Redefine para o modo atual
  const resetToCurrentMaps = () => {
    setViewMode('current');
    setActiveSubmission(null);
  };
  
  // Remove a submissão atual da visualização
  const removeCurrentSubmission = () => {
    if (!activeSubmission) return;
    
    setSubmissions(prev => prev.filter(s => s.serialNumber !== activeSubmission.serialNumber));
    setActiveSubmission(null);
    setViewMode('current');
  };
  
  // Efeito para limpar o estado ao fechar o modal
  useEffect(() => {
    if (!isOpen) {
      setActiveSubmission(null);
      setViewMode('current');
      setError(null);
    } else {
      // Quando o modal é aberto, verifica se há submissões no localStorage
      const storedSubmissions = getAllStoredSerialNumbers();
      console.log("Modal aberto - submissões armazenadas:", storedSubmissions);
      
      // Se houver submissões armazenadas e nenhuma nos recentSubmissions, tenta recuperá-las
      if (storedSubmissions.length > 0 && recentSubmissions.length === 0) {
        if (onRecoverSubmissions) {
          console.log("Recuperando submissões ao abrir modal");
          onRecoverSubmissions(storedSubmissions.slice(0, 5)); // Limita a 5
        }
      }
      
      // Verifica quantas submissões estão na lixeira
      const allSubmissions = getAllSerialNumbers();
      const trashSubmissions = allSubmissions.filter(sn => !storedSubmissions.includes(sn));
      setTrashCount(trashSubmissions.length);
    }
  }, [isOpen, onRecoverSubmissions, recentSubmissions.length]);
  
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="bg-white rounded-xl shadow-xl w-[95vw] h-[95vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-20">
              <h2 className="text-2xl font-bold text-gray-900">
                {viewMode === 'submission' && activeSubmission 
                  ? `${activeSubmission.displayName || activeSubmission.serialNumber}` 
                  : 'Resultados do Grupo 1 - Discussão'}
              </h2>
              <div className="flex items-center gap-3">
                {viewMode === 'submission' && (
                  <>
                    <button
                      onClick={resetToCurrentMaps}
                      className="p-2 hover:bg-blue-100 rounded-md transition-colors flex items-center gap-2 text-sm text-blue-600"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Voltar ao mapa atual
                    </button>
                    <button
                      onClick={removeCurrentSubmission}
                      className="p-2 hover:bg-red-100 rounded-md transition-colors flex items-center gap-2 text-sm text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                      Remover submissão
                    </button>
                  </>
                )}
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="p-4 flex-1 overflow-y-auto">
              {/* Submissões recentes do grupo */}
              {recentSubmissions.length > 0 ? (
                <div className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="flex flex-wrap justify-between items-center mb-3">
                    <h4 className="text-base font-medium text-blue-700 flex items-center">
                      <span className="inline-block w-3 h-3 rounded-full bg-blue-500 mr-2"></span>
                      Submissões recentes do seu grupo:
                    </h4>
                    
                    {/* Status do modo de visualização - movido para cá */}
                    {viewMode === 'submission' && activeSubmission && (
                      <div className="flex items-center bg-white px-3 py-1 rounded-lg border border-blue-200 shadow-sm">
                        <span className="text-sm text-blue-800 font-medium">
                          Visualizando: {activeSubmission.displayName || activeSubmission.serialNumber}
                        </span>
                        <button
                          onClick={() => {
                            setActiveSubmissionEditName(activeSubmission.displayName || '');
                            setIsEditingActiveSubmission(true);
                          }}
                          className="ml-2 p-1 hover:bg-blue-50 rounded text-blue-600"
                          title="Editar nome da submissão"
                        >
                          <Edit2 className="w-3 h-3" />
                        </button>
                        {activeSubmission.comments && (
                          <div className="ml-2 bg-blue-50 px-2 py-1 rounded-md">
                            <FileText className="w-3 h-3 text-blue-600" />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Modal de edição */}
                  {isEditingActiveSubmission && activeSubmission && (
                    <div className="mb-3 p-2 bg-white rounded-lg border border-blue-200 shadow-sm">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={activeSubmissionEditName}
                          onChange={(e) => setActiveSubmissionEditName(e.target.value)}
                          placeholder="Digite um nome para a submissão"
                          className="text-sm border border-blue-300 rounded-md px-3 py-1 flex-grow bg-white"
                          autoFocus
                        />
                        <button
                          onClick={() => {
                            if (activeSubmissionEditName.trim()) {
                              // Salvar o novo nome
                              setSubmissionDisplayName(activeSubmission.serialNumber, activeSubmissionEditName);
                              
                              // Atualizar o nome na lista local
                              setSubmissions(prev => 
                                prev.map(s => 
                                  s.serialNumber === activeSubmission.serialNumber
                                    ? { ...s, displayName: activeSubmissionEditName } 
                                    : s
                                )
                              );
                              
                              // Atualizar o nome na submissão ativa
                              setActiveSubmission({
                                ...activeSubmission,
                                displayName: activeSubmissionEditName
                              });
                              
                              // Sair do modo de edição
                              setIsEditingActiveSubmission(false);
                            }
                          }}
                          className="p-1 bg-green-100 text-green-700 border border-green-200 hover:bg-green-200 rounded-md text-sm flex items-center"
                        >
                          <Save className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingActiveSubmission(false);
                          }}
                          className="p-1 bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200 rounded-md text-sm"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      
                      {activeSubmission.comments && (
                        <div className="mt-2 text-xs text-gray-700 bg-blue-50 p-2 rounded border border-blue-100">
                          <div className="flex items-start">
                            <FileText className="w-3 h-3 text-blue-500 mt-0.5 mr-1 flex-shrink-0" />
                            <p>{activeSubmission.comments}</p>
                          </div>
                        </div>
                      )}
                      
                      <div className="mt-1 text-xs text-blue-600">
                        <span className="font-semibold">ID:</span> {activeSubmission.serialNumber} | 
                        <span className="font-semibold ml-1">Data:</span> {activeSubmission.submissionDate}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    {recentSubmissions.map((serial, idx) => {
                      // Encontrar a submissão correspondente se já estiver carregada
                      const submissionData = submissions.find(s => s.serialNumber === serial);
                      const displayName = submissionData?.displayName;
                      
                      return (
                        <SubmissionItem 
                          key={`recent-${idx}`}
                          serial={serial}
                          displayName={displayName}
                          isActive={activeSubmission?.serialNumber === serial}
                          onLoad={handleLoadSubmission}
                          onRemove={() => {
                            // Remover a submissão da lista de recentes
                            if (activeSubmission?.serialNumber === serial) {
                              setActiveSubmission(null);
                              setViewMode('current');
                            }
                            
                            // Também remover da lista de submissões carregadas
                            setSubmissions(prev => prev.filter(s => s.serialNumber !== serial));
                            
                            // Informar o componente pai para atualizar a lista
                            if (onRemoveSubmission) {
                              onRemoveSubmission(serial);
                            }
                          }}
                          onRename={(newName) => {
                            // Salvar o novo nome
                            setSubmissionDisplayName(serial, newName);
                            
                            // Atualizar o nome na lista local
                            setSubmissions(prev => 
                              prev.map(s => 
                                s.serialNumber === serial 
                                  ? { ...s, displayName: newName } 
                                  : s
                              )
                            );
                            
                            // Se esta é a submissão ativa, atualizar também lá
                            if (activeSubmission?.serialNumber === serial) {
                              setActiveSubmission({
                                ...activeSubmission,
                                displayName: newName
                              });
                            }
                          }}
                          onRecoverSubmissions={onRecoverSubmissions}
                          trashCount={trashCount}
                        />
                      );
                    })}
                  </div>
                  
                  <p className="text-xs text-blue-600 italic mt-2">
                    <span className="font-semibold">Dica:</span> Clique em uma submissão para carregá-la ou use o botão de edição para renomeá-la
                  </p>
                  
                  {submissions.length > recentSubmissions.length && (
                    <div className="mt-3 border-t border-blue-200 pt-3">
                      <button
                        onClick={() => {
                          const missingSubmissions = submissions.filter(s => 
                            !recentSubmissions.includes(s.serialNumber)
                          );
                          
                          if (missingSubmissions.length > 0 && onRecoverSubmissions) {
                            onRecoverSubmissions(missingSubmissions.map(s => s.serialNumber));
                          }
                        }}
                        className="w-full py-2 rounded-md bg-blue-100 text-blue-700 border border-blue-200 hover:bg-blue-200 transition-colors flex items-center justify-center gap-2"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Recuperar {submissions.filter(s => !recentSubmissions.includes(s.serialNumber)).length} submissões removidas
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="text-base font-medium text-blue-700 mb-3 flex items-center">
                    <span className="inline-block w-3 h-3 rounded-full bg-blue-500 mr-2"></span>
                    Sem submissões recentes
                  </h4>
                  
                  <p className="text-sm text-blue-600 mb-3">
                    Não há submissões recentes para exibir. Você pode tentar restaurar submissões anteriores.
                  </p>
                  
                  <button
                    onClick={() => {
                      // Recuperar todas as submissões armazenadas, incluindo as da lixeira
                      const allSubmissions = getAllSerialNumbers();
                      console.log("Todas as submissões disponíveis para recuperar:", allSubmissions);
                      
                      if (allSubmissions.length > 0) {
                        // Recuperar submissões da lixeira
                        const recovered = recoverSubmissions(allSubmissions);
                        console.log("Submissões recuperadas:", recovered);
                        
                        // Obter lista atualizada de todas as submissões ativas
                        const allActiveSubmissions = getAllStoredSerialNumbers();
                        
                        if (onRecoverSubmissions) {
                          onRecoverSubmissions(allActiveSubmissions);
                        }
                        
                        // Também vamos pré-carregar essas submissões para que apareçam imediatamente
                        const loadSubmissions = async () => {
                          setIsLoading(true);
                          try {
                            for (const serial of allActiveSubmissions) {
                              const submission = await fetchSubmissionBySerialNumber(serial);
                              if (submission) {
                                setSubmissions(prev => {
                                  if (!prev.some(s => s.serialNumber === submission.serialNumber)) {
                                    return [...prev, submission];
                                  }
                                  return prev;
                                });
                              }
                            }
                          } catch (error) {
                            console.error("Erro ao pré-carregar submissões:", error);
                          } finally {
                            setIsLoading(false);
                          }
                        };
                        
                        loadSubmissions();
                      } else {
                        alert("Não foram encontradas submissões anteriores para restaurar.");
                      }
                    }}
                    disabled={isLoading}
                    className={`w-full py-2 rounded-md bg-blue-100 text-blue-700 border border-blue-200 hover:bg-blue-200 transition-colors flex items-center justify-center gap-2 ${isLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
                  >
                    {isLoading ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Recuperando submissões...
                      </>
                    ) : (
                      <>
                        <History className="w-4 h-4" />
                        Restaurar todas as submissões anteriores
                      </>
                    )}
                  </button>
                </div>
              )}
              
              {/* Single active map view */}
              <div className="flex h-full relative">
                {/* Botões de navegação no lado esquerdo */}
                <div className="flex flex-col gap-3 mr-4 self-center">
                  {mapTitles.map((title, index) => (
                    <button
                      key={index}
                      className={`px-3 py-2.5 rounded-md transition-colors text-sm w-36 text-left ${
                        activeMap === index
                          ? 'bg-blue-500 text-white font-medium shadow-sm'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                      onClick={() => setActiveMap(index)}
                    >
                      {title}
                    </button>
                  ))}
                </div>
                
                {/* Mapa principal - corrigindo esticamento */}
                <div className="flex-1 mb-4 flex items-center justify-center">
                  <div 
                    className="w-full rounded-lg overflow-hidden shadow-lg bg-gray-100"
                    style={{ 
                      width: '80%',
                      maxWidth: '600px',
                      height: '450px'
                    }}
                  >
                    {/* Botão para abrir o painel de camadas */}
                    <button 
                      className="absolute top-1/2 right-0 transform -translate-y-1/2 bg-blue-500 text-white p-2 rounded-l-md shadow-md z-20 flex items-center justify-center"
                      onClick={() => setShowLayersPanel(prev => !prev)}
                      style={{ width: '32px', height: '80px' }}
                      title={showLayersPanel ? "Fechar painel de camadas" : "Ver camadas"}
                    >
                      {showLayersPanel ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M13 17l5-5-5-5M6 17l5-5-5-5"/>
                        </svg>
                      ) : (
                        <Layers className="w-5 h-5" />
                      )}
                    </button>
                    
                    {/* Área do mapa */}
                    <div className="w-full h-full">
                      <Map
                        data={{
                          ...(viewMode === 'submission' && activeSubmission ? getSubmissionMaps() : mapsData)[activeMap],
                          baseImageUrl: 
                            viewMode === 'submission' && activeSubmission
                              ? (viewMode === 'submission' && activeSubmission ? getSubmissionMaps() : mapsData)[activeMap].baseImageUrl
                              : mapStyles[mapIds[activeMap]] === 'gray' 
                                ? (viewMode === 'submission' && activeSubmission ? getSubmissionMaps() : mapsData)[activeMap].baseImageUrlGray 
                                : mapStyles[mapIds[activeMap]] === 'simple' 
                                  ? (viewMode === 'submission' && activeSubmission ? getSubmissionMaps() : mapsData)[activeMap].baseImageUrlSimple 
                                  : (viewMode === 'submission' && activeSubmission ? getSubmissionMaps() : mapsData)[activeMap].baseImageUrl
                        }}
                        onMapRef={(ref) => {
                          if (mapRefs.current) {
                            mapRefs.current[activeMap] = ref;
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Painel lateral de camadas - estilo template de email */}
                <AnimatePresence>
                  {showLayersPanel && (
                    <motion.div
                      initial={{ x: '100%', opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      exit={{ x: '100%', opacity: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="absolute top-0 right-0 bottom-0 bg-white shadow-xl border-l border-gray-200 rounded-l-lg overflow-auto z-10"
                      style={{ width: '300px', maxHeight: '100%' }}
                    >
                      <div className="p-4">
                        <div className="flex justify-between items-center mb-3">
                          <h3 className="font-medium text-gray-800 flex items-center">
                            <Layers className="w-5 h-5 text-blue-500 mr-2" />
                            Camadas
                          </h3>
                          <button 
                            className="p-1 hover:bg-gray-100 rounded-full"
                            onClick={() => setShowLayersPanel(false)}
                          >
                            <X className="w-5 h-5 text-gray-500" />
                          </button>
                        </div>
                        
                        {/* Estilo do mapa */}
                        <div className="mb-4 bg-blue-50 p-2 rounded-md">
                          <div className="flex items-center">
                            <span className="text-blue-600 font-medium">Estilo: </span>
                            <span className="ml-2">
                              {getMapStyle(activeMap) === 'color' && 'Cor'}
                              {getMapStyle(activeMap) === 'gray' && 'Cinza'}
                              {getMapStyle(activeMap) === 'simple' && 'Simplificado'}
                            </span>
                          </div>
                        </div>
                        
                        {/* Lista de camadas ativas agrupadas por seção - estilo template de email */}
                        {(() => {
                          const activeLayers = getActiveLayers(activeMap);
                          
                          if (activeLayers.length === 0) {
                            return <p className="text-gray-500 italic text-sm">Nenhuma camada selecionada</p>;
                          }
                          
                          // Agrupar camadas por seção
                          const groupedLayers: Record<string, typeof activeLayers> = {};
                          
                          activeLayers.forEach(layer => {
                            // Mapear seções antigas para novas (como no email)
                            let section = layer.section || '';
                            
                            if (section === 'Outros') section = 'Elementos de Suporte';
                            if (section === 'Transportes') section = 'Transportes Públicos';
                            if (!section && ['green-zones', 'zone30', 'volume-transito'].includes(layer.id)) {
                              section = 'Zonas a Destacar';
                            }
                            
                            if (!groupedLayers[section]) groupedLayers[section] = [];
                            groupedLayers[section].push(layer);
                          });
                          
                          // Renderizar cada grupo
                          return Object.entries(groupedLayers).map(([section, sectionLayers]) => (
                            <div key={section} className="mb-4">
                              <h4 className="text-sm font-semibold bg-blue-50 px-3 py-1.5 rounded border-l-2 border-blue-500 mb-2">
                                {section || 'Outras Camadas'}
                              </h4>
                              <ul className="flex flex-col gap-y-1 pl-2">
                                {sectionLayers.map((layer, idx) => (
                                  <li key={idx} className="text-sm flex items-center gap-1 truncate">
                                    <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                                    <span className="truncate">{layer.name}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ));
                        })()}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Componente para item de submissão
const SubmissionItem: React.FC<{
  serial: string;
  displayName?: string;
  isActive: boolean;
  onLoad: (serial: string) => void;
  onRemove: () => void;
  onRename: (newName: string) => void;
  onRecoverSubmissions?: (serialNumbers: string[]) => void;
  trashCount?: number;
}> = ({ serial, displayName, isActive, onLoad, onRemove, onRename, onRecoverSubmissions, trashCount = 0 }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(displayName || '');
  const [localTrashCount, setLocalTrashCount] = useState<number>(trashCount);
  
  // Atualizar o nome de edição quando o displayName mudar (por exemplo, ao carregar a submissão)
  useEffect(() => {
    setEditName(displayName || '');
  }, [displayName]);
  
  // Efeito para atualizar o contador de lixeira
  useEffect(() => {
    setLocalTrashCount(trashCount);
  }, [trashCount]);
  
  // Verifica quantas submissões estão na lixeira ao montar o componente
  useEffect(() => {
    const checkTrashCount = async () => {
      const allSubmissions = getAllSerialNumbers();
      const currentActive = getAllStoredSerialNumbers();
      const trashItems = allSubmissions.filter(sn => !currentActive.includes(sn));
      setLocalTrashCount(trashItems.length);
    };
    
    checkTrashCount();
  }, []);
  
  return (
    <div className="inline-block">
      <div className="flex items-center">
        <button
          onClick={() => onLoad(serial)}
          className={`text-xs px-3 py-1.5 rounded-l-full ${
            isActive
              ? 'bg-blue-100 text-blue-800 border border-blue-300'
              : 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'
          }`}
        >
          {/* Mostrar nome personalizado ou número de série */}
          {displayName || serial}
        </button>
        {/* Botão para editar o nome */}
        <button
          onClick={() => {
            setIsEditing(!isEditing);
            setEditName(displayName || '');
          }}
          className="text-xs px-2 py-1.5 bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200"
          title="Editar nome da submissão"
        >
          <Edit2 className="w-3 h-3" />
        </button>
        <button 
          onClick={onRemove}
          className="text-xs px-2 py-1.5 bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 hover:text-red-800"
          title="Remover esta submissão"
        >
          <Trash2 className="w-3 h-3" />
        </button>
        <button
          onClick={async () => {
            // Recuperar todas as submissões da lixeira
            const allSubmissions = getAllSerialNumbers();
            const currentActive = getAllStoredSerialNumbers();
            
            // Filtrar apenas as que estão na lixeira
            const trashSubmissions = allSubmissions.filter(sn => !currentActive.includes(sn));
            
            if (trashSubmissions.length > 0) {
              const recovered = recoverSubmissions(trashSubmissions);
              if (recovered.length > 0 && onRecoverSubmissions) {
                onRecoverSubmissions(recovered);
                setLocalTrashCount(0); // Zeramos o contador após recuperar
              }
            } else {
              alert("Não existem submissões na lixeira para recuperar.");
            }
          }}
          className={`text-xs px-2 py-1.5 rounded-r-full ${
            localTrashCount > 0 
              ? 'bg-green-100 text-green-700 border border-green-300' 
              : 'bg-green-50 text-green-600 border border-green-200'
          } hover:bg-green-100 hover:text-green-800 relative`}
          title="Restaurar submissões da lixeira"
          disabled={localTrashCount === 0}
        >
          <History className="w-3 h-3" />
          {localTrashCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-green-500 text-white rounded-full text-[8px] min-w-[12px] h-3 flex items-center justify-center">
              {localTrashCount}
            </span>
          )}
        </button>
      </div>
      
      {/* Campo de edição do nome */}
      {isEditing && (
        <div className="mt-1 flex absolute z-10 bg-white shadow-md rounded-md p-1">
          <input
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            placeholder="Digite um nome para a submissão"
            className="text-xs border border-gray-300 rounded-l-md px-2 py-1 flex-grow"
            autoFocus
          />
          <button
            onClick={() => {
              if (editName.trim()) {
                // Chamar a função de renomeação
                onRename(editName);
                // Sair do modo de edição
                setIsEditing(false);
              }
            }}
            className="text-xs px-2 py-1 bg-green-100 text-green-700 border border-green-200 hover:bg-green-200 rounded-r-md flex items-center"
          >
            <Save className="w-3 h-3 mr-1" />
            Salvar
          </button>
        </div>
      )}
    </div>
  );
};

export default GroupResultsModal; 